if ( typeof TooltipTrigger !== 'function' ) {

	class TooltipTrigger extends HTMLElement {

		constructor() {
			super();

			this.trigger = this.querySelector('button');
			this.label = this.querySelector('.text-with-icon__label');

			this.onTriggerClick = this.onTriggerClick.bind(this);
			this.onDocumentClick = this.onDocumentClick.bind(this);
			this.onKeydown = this.onKeydown.bind(this);
			this.onResize = this.onResize.bind(this);
			this.position = this.position.bind(this);

			this.trigger?.addEventListener('click', this.onTriggerClick);
			this.addEventListener('mouseenter', this.position);
			this.addEventListener('focusin', this.position);
		}

		connectedCallback() {
			// custom elements upgrade the moment they're connected to the document,
			// whether that's the initial page render or markup injected later (quick
			// view, ajax cart, etc.) -- so positioning + listeners are always wired up
			// correctly with no separate "re-run this on ajax content" logic needed.
			this.position();

			document.addEventListener('click', this.onDocumentClick);
			document.addEventListener('keydown', this.onKeydown);
			window.addEventListener('resize', this.onResize);

			// text metrics can shift once web fonts swap in, which can change the
			// tooltip's width enough to need a re-check.
			document.fonts?.ready?.then(this.position);
		}

		disconnectedCallback() {
			document.removeEventListener('click', this.onDocumentClick);
			document.removeEventListener('keydown', this.onKeydown);
			window.removeEventListener('resize', this.onResize);
			clearTimeout(this.resizeTimer);
		}

		onTriggerClick(event) {
			event.preventDefault();
			event.stopPropagation();
			this.classList.contains('is-open') ? this.close() : this.open();
		}

		onDocumentClick(event) {
			if ( ! this.contains(event.target) ) this.close();
		}

		onKeydown(event) {
			if ( event.key === 'Escape' && this.classList.contains('is-open') ) {
				this.close();
				this.trigger?.focus();
			}
		}

		onResize() {
			clearTimeout(this.resizeTimer);
			this.resizeTimer = setTimeout(this.position, 150);
		}

		open() {
			document.querySelectorAll('tooltip-trigger.is-open').forEach((el) => {
				if ( el !== this ) el.close();
			});

			this.position();
			this.classList.add('is-open');
			this.trigger?.setAttribute('aria-expanded', 'true');
		}

		close() {
			this.classList.remove('is-open');
			this.trigger?.setAttribute('aria-expanded', 'false');
		}

		// the tooltip bubble defaults to centered on the trigger (see --tooltip-shift
		// in _updates.scss). this only nudges it back in when it would cross the
		// viewport edge -- so it stays correct for any tooltip_info length on any
		// screen size, instead of a fixed offset tuned for one combination of the two.
		position() {
			if ( ! this.label ) return;

			this.label.style.setProperty('--tooltip-shift', '0px');

			const margin = 8;
			const viewportWidth = document.documentElement.clientWidth;
			const rect = this.label.getBoundingClientRect();

			let shift = 0;
			if ( rect.left < margin ) {
				shift = margin - rect.left;
			} else if ( rect.right > viewportWidth - margin ) {
				shift = (viewportWidth - margin) - rect.right;
			}

			this.label.style.setProperty('--tooltip-shift', `${shift}px`);
		}

	}

	if ( typeof customElements.get('tooltip-trigger') === 'undefined' ) {
		customElements.define('tooltip-trigger', TooltipTrigger);
	}

}
